import './ffs-parameter-input.css'

import {
  CheckCircleTwoTone,
  StopTwoTone,
  TableOutlined,
} from '@ant-design/icons'
import {
  Button,
  ConfigProvider,
  InputNumber,
  Select,
  Tooltip,
  Typography,
} from 'antd'
import { useGeckoLocale } from 'features/locale'
import _ from 'lodash'
import React, {
  CSSProperties,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

import { LABEL_WIDTH } from './FfsCalcLayout'

export type MeasurementType = 'thickness' | 'ratio' | 'pressure' | 'temperature'

const { Text } = Typography

const ICON_STYLE: React.CSSProperties = {
  fontSize: '24px',
}

const RenderAfterContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  padding: 0;
`

const UNITS: Partial<Record<MeasurementType, readonly GeckoMeasurementUnit[]>> =
  {
    thickness: [
      GeckoMeasurementUnit.Inches,
      GeckoMeasurementUnit.Feet,
      GeckoMeasurementUnit.Millimeters,
      GeckoMeasurementUnit.Meters,
    ],
    pressure: [
      GeckoMeasurementUnit.Megapascals,
      GeckoMeasurementUnit.PoundsForcePerSqInch,
    ],
    temperature: [
      GeckoMeasurementUnit.Fahrenheit,
      GeckoMeasurementUnit.Celsius,
    ],
  }

export interface FfsParameterInputFieldProps {
  label: ReactNode
  labelTooltip?: ReactNode
  measurementType?: MeasurementType
  labelWidth?: CSSProperties['width']
  value: GeckoMeasurement | null
  setValue?: Dispatch<SetStateAction<GeckoMeasurement | null>>
  isValid?: boolean
  min?: GeckoMeasurement
  max?: GeckoMeasurement
  step?: number
  trailingText?:
    | string
    | ((value: GeckoMeasurement | null) => string | undefined)
  onLookup?: () => void
}

const Container = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  justify-content: left;
  width: 100%;
  aligh-items: center;
  gap: 1rem;
`

const RawFfsParameterField: FC<FfsParameterInputFieldProps> = ({
  label,
  labelTooltip,
  measurementType = 'thickness',
  labelWidth = LABEL_WIDTH,
  value,
  setValue,
  isValid,
  min,
  max,
  step,
  onLookup,
  trailingText,
}) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()
  const [unit, setUnit] = useState<GeckoMeasurementUnit | null>(null)
  const allowableUnits = useMemo(
    () => UNITS[measurementType] ?? [],
    [measurementType]
  )
  useEffect(() => {
    if (allowableUnits.length) {
      setUnit(getPreferredMeasurementUnit(allowableUnits[0]))
    }
  }, [allowableUnits, getPreferredMeasurementUnit, measurementType])

  const unitOptions = useMemo(
    () =>
      allowableUnits.map((unit) => ({
        value: unit.id,
        label: unit.name,
      })),
    [allowableUnits]
  )

  const lookupAfter = useMemo(
    () =>
      onLookup && (
        <Button
          shape='circle'
          icon={<TableOutlined />}
          onClick={onLookup}
          size='small'
          type='text'
        />
      ),
    [onLookup]
  )
  const selectAfter = useMemo(
    () =>
      unit && (
        <Select
          value={unit.id}
          options={unitOptions}
          onChange={(value) => setUnit(GeckoMeasurementUnit.GetByID(value)!)}
          style={{ width: 80 }}
        />
      ),
    [unit, unitOptions]
  )

  const trailingTextVal = useMemo(
    () =>
      typeof trailingText === 'function'
        ? trailingText(value?.convertTo(unit ?? value.unit) ?? null)
        : trailingText,
    [trailingText, unit, value]
  )

  const renderAfter = useMemo(() => {
    if (!lookupAfter && !selectAfter && !trailingTextVal) {
      return undefined
    }
    return (
      <RenderAfterContainer>
        {trailingTextVal ? (
          <Text
            style={{
              color: CantileverTheme.colors.grayscale[300],
              fontSize: '0.7rem',
            }}>
            {trailingTextVal}
          </Text>
        ) : undefined}
        {lookupAfter ? lookupAfter : undefined}
        {selectAfter ? selectAfter : undefined}
      </RenderAfterContainer>
    )
  }, [lookupAfter, selectAfter, trailingTextVal])

  const checkNewValue = useCallback(
    (value: GeckoMeasurement | null) => {
      if (value === null) {
        return true
      }
      if (min && min.convertTo(value.unit).num > value.num) {
        return false
      }
      if (max && max.convertTo(value.unit).num < value.num) {
        return false
      }
      return true
    },
    [max, min]
  )

  return (
    <ConfigProvider
      theme={{
        components: {
          InputNumber: {
            colorBgContainer:
              setValue || onLookup
                ? CantileverTheme.colors.grayscale[600]
                : CantileverTheme.colors.grayscale[700],
          },
        },
      }}>
      <Container>
        <Tooltip title={labelTooltip}>
          <Text style={{ width: labelWidth ?? '30%' }}>{label}</Text>
        </Tooltip>
        <InputNumber
          style={{ width: '70%' }}
          value={
            _.isNil(value)
              ? null
              : +value?.convertTo(unit ?? value.unit).num.toFixed(2)
          }
          onChange={(num) => {
            if (!setValue) {
              return
            }
            const newValue =
              num === null
                ? null
                : GeckoMeasurement.CreateFromValue(
                    num,
                    unit ?? value?.unit ?? GeckoMeasurementUnit.Millimeters
                  )
            if (checkNewValue(newValue)) {
              setValue(newValue)
            }
          }}
          readOnly={!setValue}
          addonAfter={renderAfter}
          status={isValid === false ? 'error' : undefined}
          step={step}
        />
        {isValid !== undefined && (
          <>
            {isValid ? (
              <CheckCircleTwoTone twoToneColor={'green'} style={ICON_STYLE} />
            ) : (
              <StopTwoTone twoToneColor={'red'} style={ICON_STYLE} />
            )}
          </>
        )}
      </Container>
    </ConfigProvider>
  )
}
RawFfsParameterField.displayName = 'FfsParameterField'

export const FfsParameterField = React.memo(RawFfsParameterField)
